import React, { useState, useEffect, useRef } from "react";
import {
    Dropdown,
    Popup,
    Icon,
    Image,
    Menu,
    Header,
    Grid,
} from "semantic-ui-react";
import isUndefined from "lodash/isUndefined";
import isEmpty from "lodash/isEmpty";
import each from "lodash/each";
import sortBy from "lodash/sortBy";
import SoundPlayer from "../player/SoundPlayer";
import AutoplayerButton from "./../notation/AutoplayerButton";
import RangeIcon from "../../assets/icons/player/range.svg";
import SpeedIcon from "../../assets/icons/player/speed.svg";
import VariationIcon from "../../assets/icons/player/sheet.svg";
import Banjo from "../../assets/icons/instruments/banjo.svg";
import Bassoon from "../../assets/icons/instruments/Bassoon.svg";
import Clarinet from "../../assets/icons/instruments/Clarinet.svg";
import Electricguitar from "../../assets/icons/instruments/Electricguitar.svg";
import Flute from "../../assets/icons/instruments/Flute.svg";
import Glockenspiel from "../../assets/icons/instruments/glockenspiel.svg";
import Guitar from "../../assets/icons/instruments/guitar.svg";
import Harp from "../../assets/icons/instruments/harp.svg";
import Harpsichord from "../../assets/icons/instruments/Harpsichord.svg";
import Oboe from "../../assets/icons/instruments/Oboe.svg";
import Panflute from "../../assets/icons/instruments/PanFlute.svg";
import Piano from "../../assets/icons/instruments/piano.svg";
import Pizzicato from "../../assets/icons/instruments/Pizzicato.svg";
import TinkleBell from "../../assets/icons/instruments/TinkleBell.svg";
import Violin from "../../assets/icons/instruments/Violin.svg";
import PlayIcon from "../../assets/icons/player/play.svg";
import StopIcon from "../../assets/icons/player/stop.svg";

//instrument displayName is the key
const instrumentImages = {
    Banjo: Banjo,
    Bassoon: Bassoon,
    Clarinet: Clarinet,
    "Electric Guitar": Electricguitar,
    Flute: Flute,
    Glockenspiel: Glockenspiel,
    Guitar: Guitar,
    Harp: Harp,
    Harpsichord: Harpsichord,
    Oboe: Oboe,
    "Pan Flute": Panflute,
    Piano: Piano,
    "Tinkle Bell": TinkleBell,
    Violin: Violin,
    Pizzicato: Pizzicato,
};

const NotationHeader = ({
    instruments: propsInstruments,
    notation: propsNotation,
    variation: propsVariation,
    selectedTempo: propsSelectedTempo,
    bus,
    notifyInstrumentLoadingState,
    stopAnimation,
    variationChanged,
    tempoChanged,
    playAnimation,
    pauseAnimation,
    demoplayer,
    currentSongIndex,
    songsCount,
    jiggleLeft,
    jiggleRight,
    onPreviousSong,
    onNextSong,
}) => {
    const [instruments, setInstruments] = useState(propsInstruments);
    const [notation, setNotation] = useState(propsNotation);
    const [variation, setVariation] = useState(propsVariation);
    const [selectedTempo, setSelectedTempo] = useState(propsSelectedTempo);
    const [selectedInstrumentIndex, setSelectedInstrumentIndex] =
        useState(undefined);
    const [trackingPopoverIsOpen, setTrackingPopoverIsOpen] = useState(false);
    const [playTrackerActive, setPlayTrackerActive] = useState(true);
    const [selectedRange, setSelectedRange] = useState(undefined);
    const soundPlayerRef = useRef(null);

    useEffect(() => {
        setInstruments(propsInstruments);
        setNotation(propsNotation);
        setVariation(propsVariation);
        // Keep user preferences (tempo, range, instrument) when changing songs
        if (propsSelectedTempo !== undefined) {
            setSelectedTempo(propsSelectedTempo);
        }
    }, [propsNotation.id, propsVariation, propsSelectedTempo]);

    const instrumentContentHeader = (name) => {
        return <Header as="h5" image={instrumentImages[name]} content={name} />;
    };

    const playTrackerChanged = (value) => {
        setPlayTrackerActive(!value);
    };

    const instrumentChanged = (event, data) => {
        setSelectedInstrumentIndex(data.value);
    };

    const variationChangedHandler = (event, data) => {
        //tell DynamicNotationContainer that variation has changed
        stopAnimation();
        variationChanged(event, data);
        setSelectedInstrumentIndex(undefined);
    };

    const tempoChangedHandler = (event, data) => {
        tempoChanged(event, data);
    };

    const trackerChanged = (val) => {
        if (val === "Play") {
            soundPlayerRef.current.playCountIn(
                selectedTempo || variation.tempo,
                variation.signature
            );
            playAnimation(selectedTempo || variation.tempo, variation);
        } else if (val === "Stop") {
            stopAnimation();
        } else if (val === "Pause") {
            pauseAnimation();
        }
    };

    const updateSoundPlayerRange = (event, data) => {
        soundPlayerRef.current.loadKeyMappings(data.value);
        setSelectedRange(data.value);
    };

    const updateVariationNotes = (variation) => {
        setVariation(variation);
    };

    const handleOpen = () => {
        setTrackingPopoverIsOpen(true);
    };

    const handleClose = () => {
        setTrackingPopoverIsOpen(false);
    };

    const showInstrumentsWidget = (defaultInstrument) => {
        let options = [];
        each(instruments, (item, index) => {
            if (!isUndefined(instrumentImages[item.displayName])) {
                options.push({
                    value: index,
                    content: instrumentContentHeader(item.displayName),
                });
            }
        });

        const instrumentTrigger = (
            <Menu.Item>
                <Icon size="large">
                    {" "}
                    <Image
                        centered
                        src={instrumentImages[defaultInstrument.displayName]}
                    />
                </Icon>
                <span className="mobile-hide mtudeIconButton iconFont">
                    {" "}
                    {defaultInstrument.displayName}
                </span>
            </Menu.Item>
        );

        return (
            <Dropdown
                className="instrumentDropdown"
                trigger={instrumentTrigger}
                onChange={instrumentChanged}
                pointing="top right"
                icon={null}
                options={options}
                value={defaultInstrument.index}
            />
        );
    };

    const showVariationWidget = () => {
        let options = [];
        each(notation.variations, function (item, index) {
            options.push({ text: item.type.displayValue, value: index });
        });
        const variationTrigger = (
            <Menu.Item>
                <Icon size="large">
                    {" "}
                    <Image centered src={VariationIcon} />
                </Icon>
                <span className="mobile-hide mtudeIconButton iconFont">
                    {" "}
                    {variation.type.displayValue}{" "}
                </span>
            </Menu.Item>
        );

        return (
            <Dropdown
                trigger={variationTrigger}
                onChange={variationChangedHandler}
                pointing="top right"
                icon={null}
                options={options}
                defaultValue={options[0].value}
            />
        );
    };

    const showRangeSelectionWidget = (defaultRange) => {
        const RANGE_LABEL = "Range ";
        let options = [];
        for (let i = 5; i >= 1; i--) {
            options.push({ text: RANGE_LABEL + i, value: i });
        }

        const rangeTrigger = (
            <Menu.Item>
                <Icon size="large">
                    {" "}
                    <Image centered src={RangeIcon} />
                </Icon>
                <span className="mobile-hide mtudeIconButton iconFont">
                    {RANGE_LABEL}
                </span>
            </Menu.Item>
        );

        return (
            <Dropdown
                key={`range-${notation.id}`}
                trigger={rangeTrigger}
                onChange={updateSoundPlayerRange}
                pointing="top left"
                icon={null}
                options={options}
                value={selectedRange || defaultRange}
            />
        );
    };

    const showTempoWidget = (defaultTempo) => {
        const options = [
            { text: "Very Slow", value: Math.round(defaultTempo * 0.5) },
            { text: "Slow", value: Math.round(defaultTempo * 0.8) },
            { text: "Normal", value: Math.round(defaultTempo) },
            { text: "Fast", value: Math.round(defaultTempo * 1.5) },
            { text: "Very Fast", value: Math.round(defaultTempo * 2) },
        ];

        const tempoTrigger = (
            <Menu.Item>
                <Icon size="large">
                    <Image centered src={SpeedIcon} />
                </Icon>
                <span className="mobile-hide mtudeIconButton iconFont">
                    Speed
                </span>
            </Menu.Item>
        );

        return (
            <Dropdown
                key={`tempo-${notation.id}`}
                onChange={tempoChangedHandler}
                trigger={tempoTrigger}
                pointing="top left"
                icon={null}
                options={sortBy(options, "value").reverse()}
                value={selectedTempo || defaultTempo}
            />
        );
    };

    const showTrackingMenu = () => {
        const tempoTrigger = (
            <Menu.Item>
                <Icon
                    className="info"
                    size="large"
                    style={{ color: "#b874cc" }}
                />
                <span className="mobile-hide mtudeIconButton iconFont">
                    Tracking
                </span>
            </Menu.Item>
        );

        return (
            <Popup
                style={{ height: 70 }}
                trigger={tempoTrigger}
                open={trackingPopoverIsOpen}
                onClose={handleClose}
                onOpen={handleOpen}
                on="click"
                position="bottom left"
            >
                <Grid.Row>
                    <Grid.Column>
                        <span
                            className="mtudeIconButton"
                            onClick={() => {
                                trackerChanged("Play");
                                handleClose();
                            }}
                        >
                            <Icon size="large">
                                {" "}
                                <Image centered src={PlayIcon} />{" "}
                            </Icon>{" "}
                            Start
                        </span>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column>
                        <span
                            className="mtudeIconButton"
                            onClick={() => {
                                trackerChanged("Stop");
                                handleClose();
                            }}
                        >
                            <Icon size="large">
                                {" "}
                                <Image centered src={StopIcon} />{" "}
                            </Icon>{" "}
                            Stop
                        </span>
                    </Grid.Column>
                </Grid.Row>
            </Popup>
        );
    };

    // use the selected instrument, or the default one if the user has not selected an
    // instrument yet
    const instrument =
        instruments[
            selectedInstrumentIndex !== undefined
                ? selectedInstrumentIndex
                : variation.instrument.index
        ];

    if (isEmpty(notation.variations)) {
        return <div />;
    }

    return (
        <Menu
            secondary
            fluid
            icon="labeled"
            borderless
            className="playerMenu noMarginBorder"
            style={{ position: "relative" }}
        >
            <SoundPlayer
                bus={bus}
                notifyInstrumentLoadingState={notifyInstrumentLoadingState}
                instrument={instrument}
                variations={notation.variations}
                variation={variation}
                notationId={notation.id}
                onRef={(ref) => (soundPlayerRef.current = ref)}
                playAnimation={playAnimation}
                demoplayer={demoplayer}
                stopAnimation={stopAnimation}
                tempo={selectedTempo}
                trackerIsOn={playTrackerActive}
            />
            {showTempoWidget(variation.tempo)}
            {showRangeSelectionWidget(variation.range)}
            {
                <AutoplayerButton
                    bus={bus}
                    soundplayer={soundPlayerRef.current}
                    variation={variation}
                    variations={notation.variations}
                    playTrackerChanged={playTrackerChanged}
                    playTrackerActive={playTrackerActive}
                />
            }
            {showTrackingMenu(variation.tempo)}

            {demoplayer && currentSongIndex !== undefined && (
                <div
                    style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                        fontSize: "13px",
                        zIndex: 10,
                        marginLeft: "0",
                    }}
                >
                    <span
                        className={`nav-button ${jiggleLeft ? "jiggle" : ""} ${
                            currentSongIndex === 0 ? "disabled" : ""
                        }`}
                        onClick={onPreviousSong}
                        style={{
                            cursor:
                                currentSongIndex === 0
                                    ? "not-allowed"
                                    : "pointer",
                            opacity: currentSongIndex === 0 ? 0.5 : 1,
                            padding: "4px 8px",
                            fontWeight: 600,
                            transition: "all 0.3s ease",
                            color: "#b874cc",
                        }}
                    >
                        ← Previous Song
                    </span>
                    <span
                        style={{
                            margin: "0 8px",
                            fontSize: "16px",
                            opacity: 0.6,
                        }}
                    >
                        •
                    </span>
                    <span
                        className={`nav-button ${jiggleRight ? "jiggle" : ""} ${
                            currentSongIndex === songsCount - 1
                                ? "disabled"
                                : ""
                        }`}
                        onClick={onNextSong}
                        style={{
                            cursor:
                                currentSongIndex === songsCount - 1
                                    ? "not-allowed"
                                    : "pointer",
                            opacity:
                                currentSongIndex === songsCount - 1 ? 0.5 : 1,
                            padding: "4px 8px",
                            fontWeight: 600,
                            transition: "all 0.3s ease",
                            color: "#b874cc",
                        }}
                    >
                        Next Song →
                    </span>
                </div>
            )}

            <Menu.Menu position="right">
                {notation.variations.length > 1 ? showVariationWidget() : ""}
                {showInstrumentsWidget(instrument)}
            </Menu.Menu>
        </Menu>
    );
};

export default NotationHeader;
